import * as THREE from 'three';
// import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import CameraControls from 'camera-controls';
// import { ViewHelper } from 'three/addons/helpers/ViewHelper.js';
import { CSS2DRenderer } from 'three/addons/renderers/CSS2DRenderer.js';

import { Node } from "./Viewer.Node.js";
import { Support } from "./Viewer.Support.js";
import { Member } from "./Viewer.Member.js";
import { Hinge } from "./Viewer.Hinge.js";
import { ForceHelper } from "./Viewer.ForceHelper.js";
import { MomentHelper } from "./Viewer.MomentHelper.js";
import { DistributedLoadHelper } from "./Viewer.DistributedLoadHelper.js";
import { InternalForceHelper } from "./Viewer.InternalForceHelper.js";
import { DeformedStructureHelper } from "./Viewer.DeformedStructureHelper.js";
import { ViewerConfig } from "./Viewer.Config";
import { MaterialManager } from "./Viewer.MaterialManager";
import { InfiniteGridHelper } from "./Viewer.InfiniteGridHelper.js";
import { LinearDimension } from "./Viewer.LinearDimension";

CameraControls.install( {THREE: THREE} );

THREE.Object3D.DEFAULT_UP.set(0, 0, 1);

class Viewer {
    constructor(container) {
        this.container = container;
        this.config = new ViewerConfig();
        this.materialManager = new MaterialManager(this.config);

        this.nodesMap = new Map();
        this.supportsMap = new Map();
        this.membersMap = new Map();
        this.dimensionsMap = new Map();

        this.pointLoads = [];
        this.pointLoadValues = [];
        this.memberDistributedLoads = [];
        this.memberDistributedLoadValues = [];
        this.reactions = [];
        this.reactionValues = [];

        this.results = {
            axialForces: [],
            axialForcesValues: [],
            shearForces: [],
            shearForcesValues: [],
            bendingMoments: [],
            bendingMomentsValues: [],
            deformations: [],
            deformationValues: [],
        };

        this.scene = new THREE.Scene();

        // Orthographic Camera
        const aspectRatio = this.container.clientWidth / this.container.clientHeight;
        this.frustumSize = 10;
        this.camera = new THREE.OrthographicCamera(this.frustumSize * aspectRatio / -2, this.frustumSize * aspectRatio / 2, this.frustumSize / 2, this.frustumSize / -2, 0.1, 2000);

        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true
        });
        this.renderer.setClearColor(0xffffff, 0);

        this.labelRenderer = new CSS2DRenderer();

        this.initialize();

        this.debouncedResize = this.debounce(this.onContainerResize.bind(this), 0.002);

        this.resizeObserver = new ResizeObserver(() => {
            this.debouncedResize();
        });

        this.resizeObserver.observe(this.container);

    }


    initialize() {
        this.camera.position.set(0, -10, 0);
        this.camera.lookAt(0, 0, 0);

        this.renderer.autoClear = false;
        this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
        this.container.appendChild(this.renderer.domElement);

        this.labelRenderer.domElement.style.position = 'absolute';
        this.labelRenderer.domElement.style.top = '0px';
        this.labelRenderer.domElement.style.pointerEvents = 'none';
        this.container.appendChild(this.labelRenderer.domElement);

        this.grid = new InfiniteGridHelper(0.5, 5, new THREE.Color('#868686'), 1000, 'xzy');

        this.scene.add(this.grid);

        this.axesHelperOrigin = new THREE.AxesHelper();
        let colors = this.axesHelperOrigin.geometry.attributes.color;
        colors.setXYZ(0, 1, 0, 0); // index, R, G, B
        colors.setXYZ(1, 1, 0, 0); // red
        colors.setXYZ(2, 0, 1, 0);
        colors.setXYZ(3, 0, 1, 0); // green
        colors.setXYZ(4, 0, 0, 1);
        colors.setXYZ(5, 0, 0, 1); // blue
        this.scene.add(this.axesHelperOrigin);

        this.cameraControls = new CameraControls(this.camera, this.renderer.domElement);
        this.cameraControls.setTarget(0, 0, 0); // view direction perpendicular to XY plane
        this.cameraControls.minPolarAngle = Math.PI / 2;
        this.cameraControls.maxPolarAngle = Math.PI / 2;
        this.cameraControls.mouseButtons.left = CameraControls.ACTION.TRUCK;
        this.cameraControls.mouseButtons.middle = CameraControls.ACTION.TRUCK;
        this.cameraControls.mouseButtons.right = CameraControls.ACTION.TRUCK;
        this.cameraControls.smoothTime = 0.05;
        this.cameraControls.draggingSmoothTime = 0.05;
        this.renderer.domElement.addEventListener('dblclick', () => {this.zoomToFit(this.scene);});
        this.cameraControls.touches.one = CameraControls.ACTION.TOUCH_TRUCK;
        // this.cameraControls.touches.two = CameraControls.ACTION.TOUCH_TRUCK;
        this.cameraControls.touches.three = CameraControls.ACTION.TOUCH_TRUCK;
        // this.renderer.domElement.addEventListener('doubletap', () => {this.zoomToFit(this.scene);});

        // this.viewHelper = new ViewHelper(this.camera, this.renderer.domElement);

        this.clock = new THREE.Clock();

        this.setupConfigListeners();
        this.applySettings();

        this.animate();
    }

    animate() {
        const delta = this.clock.getDelta();
        requestAnimationFrame(this.animate.bind(this));
        this.renderer.clear();
        this.renderer.render(this.scene, this.camera);
        this.labelRenderer.render(this.scene, this.camera);
        // this.viewHelper.render(this.renderer);
        this.cameraControls.update( delta );
    }

    onContainerResize() {
        const width = this.container.clientWidth;
        const height = this.container.clientHeight;

        const aspect = width / height;

        this.camera.left = -this.frustumSize * aspect / 2;
        this.camera.right = this.frustumSize * aspect / 2;
        this.camera.top = this.frustumSize / 2;
        this.camera.bottom = -this.frustumSize / 2;
        this.camera.updateProjectionMatrix();

        this.renderer.setSize(width, height);
        this.labelRenderer.setSize(width, height);
    }

    debounce(func, delay) {
        let timer;
        return function (...args) {
            clearTimeout(timer);
            timer = setTimeout(() => {
                func.apply(this, args);
            }, delay);
        };
    }

    applySettings() {
        this.setGridVisibility(this.config.settings.showGrid);
        this.setOriginVisibility(this.config.settings.showOrigin);
        this.setNodesVisibility(this.config.settings.showNodes);
        this.setNodeLabelsVisibility(this.config.settings.showNodeLabels);
        this.setSupportsVisibility(this.config.settings.showSupports);
        this.setSupportsScale(this.config.settings.supportsScale);
        this.setSupportsColor(this.config.settings.supportsColor);
        this.setMembersVisibility(this.config.settings.showMembers);
        this.setMemberLabelsVisibility(this.config.settings.showMemberLabels);
        this.setMemberLocalAxisVisibility(this.config.settings.showMemberLocalAxis);
        this.setForcesVisibility(this.config.settings.showForces);
        this.setForcesScale(this.config.settings.forcesScaleFactor);
        this.setForcesColor(this.config.settings.forcesColor);
        this.setReactionsVisibility(this.config.settings.showReactions);
        this.setReactionsScale(this.config.settings.reactionsScaleFactor);
        this.setReactionsColor(this.config.settings.reactionsColor);
        this.setAxialForcesVisibility(this.config.settings.showAxialForces);
        this.setAxialForcesScale(this.config.settings.axialForcesScaleFactor);
        this.setAxialForcesColor(this.config.settings.axialForcesColor);
        this.setShearForcesVisibility(this.config.settings.showShearForces);
        this.setShearForcesScale(this.config.settings.shearForcesScaleFactor);
        this.setShearForcesColor(this.config.settings.shearForcesColor);
        this.setBendingMomentsVisibility(this.config.settings.showBendingMoments);
        this.setBendingMomentsScale(this.config.settings.bendingMomentsScaleFactor);
        this.setBendingMomentsColor(this.config.settings.bendingMomentsColor);
        this.setDeformationsVisibility(this.config.settings.showDeformations);
        this.setDeformationsScale(this.config.settings.deformationsScaleFactor);
        this.setDeformationsColor(this.config.settings.deformationsColor);
        if (this.config.settings.autoFit) {
            this.zoomToFit(this.scene);
        }
    }

    setupConfigListeners() {
        this.config.addListener('showGrid', (visibility) => {
            this.setGridVisibility(visibility);
        });

        this.config.addListener('showOrigin', (visibility) => {
            this.setOriginVisibility(visibility);
        });

        this.config.addListener('showNodes', (visibility) => {
            this.setNodesVisibility(visibility);
        });

        this.config.addListener('showNodeLabels', (visibility) => {
            this.setNodeLabelsVisibility(visibility);
        });

        this.config.addListener('showSupports', (visibility) => {
            this.setSupportsVisibility(visibility);
        });

        this.config.addListener('supportsScale', (newScale) => {
            this.setSupportsScale(newScale);
        });

        this.config.addListener('supportsColor', (newColor) => {
            this.setSupportsColor(newColor);
            this.materialManager.updateMaterials();
        });

        this.config.addListener('showMembers', (visibility) => {
            this.setMembersVisibility(visibility);
        });

        this.config.addListener('showMemberLabels', (visibility) => {
            this.setMemberLabelsVisibility(visibility);
        });

        this.config.addListener('showMemberLocalAxis', (visibility) => {
            this.setMemberLocalAxisVisibility(visibility);
        });

        this.config.addListener('showForces', (visibility) => {
            this.setForcesVisibility(visibility);
        });


        this.config.addListener('forcesScaleFactor', (newScale) => {
            this.setForcesScale(newScale);
        });

        this.config.addListener('forcesColor', (newColor) => {
            this.setForcesColor(newColor);
        });

        this.config.addListener('showReactions', (visibility) => {
            this.setReactionsVisibility(visibility);
        });

        this.config.addListener('reactionsScaleFactor', (newScale) => {
            this.setReactionsScale(newScale);
        });

        this.config.addListener('reactionsColor', (newColor) => {
            this.setReactionsColor(newColor);
        });

        this.config.addListener('showAxialForces', (visibility) => {
            this.setAxialForcesVisibility(visibility);
        });

        this.config.addListener('axialForcesScaleFactor', (newScale) => {
            this.setAxialForcesScale(newScale);
        });

        this.config.addListener('axialForcesColor', (newColor) => {
            this.setAxialForcesColor(newColor);
        });

        this.config.addListener('showShearForces', (visibility) => {
            this.setShearForcesVisibility(visibility);
        });

        this.config.addListener('shearForcesScaleFactor', (newScale) => {
            this.setShearForcesScale(newScale);
        });

        this.config.addListener('shearForcesColor', (newColor) => {
            this.setShearForcesColor(newColor);
        });

        this.config.addListener('showBendingMoments', (visibility) => {
            this.setBendingMomentsVisibility(visibility);
        });

        this.config.addListener('bendingMomentsScaleFactor', (newScale) => {
            this.setBendingMomentsScale(newScale);
        });

        this.config.addListener('bendingMomentsColor', (newColor) => {
            this.setBendingMomentsColor(newColor);
        });

        this.config.addListener('showDeformations', (visibility) => {
            this.setDeformationsVisibility(visibility);
        });

        this.config.addListener('deformationsScaleFactor', (newScale) => {
            this.setDeformationsScale(newScale);
        });

        this.config.addListener('deformationsColor', (newColor) => {
            this.setDeformationsColor(newColor);
        });
    }

    setGridVisibility(visibility) {
        this.grid.visible = visibility;
    }

    setOriginVisibility(visibility) {
        this.axesHelperOrigin.visible = visibility;
    }

    setNodesVisibility(visibility) {
        this.nodesMap.forEach((node) => {
            node.visible = visibility;
        });
    }

    setNodeLabelsVisibility(visibility) {
        this.nodesMap.forEach((node) => {
            node.label.visible = visibility;
        });
    }

    setSupportsVisibility(visibility) {
        this.supportsMap.forEach((support) => {
            support.visible = visibility;
        });

        this.membersMap.forEach(member => {
            member.children.forEach(children => {
                if (children instanceof Hinge) {
                    children.visible = visibility;
                }
            });
        });
    }

    setSupportsScale(newScale) {
        this.supportsMap.forEach((support) => {
            support.setScale( newScale  * 0.2 );
        });

        this.membersMap.forEach(member => {
            member.children.forEach(children => {
                if (children instanceof Hinge) {
                    children.setScale( newScale * 0.15 );
                }
            });
        });
    }

    setSupportsColor(color) {
        this.config.updateSetting('supportsColor', color);

        this.membersMap.forEach(member => {
            member.children.forEach(children => {
                if (children instanceof Hinge) {
                    children.setColor( color );
                }
            });
        });
    }

    setMembersVisibility( visibility ) {
        this.membersMap.forEach((member) => {
            member.visible = visibility;
        });
    }

    setMemberLabelsVisibility( visibility ) {
        this.membersMap.forEach((member) => {
            member.label.visible = visibility;
        });
    }

    setMemberLocalAxisVisibility( visibility ) {
        this.membersMap.forEach((member) => {
            member.localAxes.visible = visibility;
        });
    }
    //
    setForcesVisibility( visibility ) {
        this.pointLoads.forEach(load => {
            load.visible = visibility;
            load.label.visible = visibility;
        });

        this.memberDistributedLoads.forEach(load => {
            load.visible = visibility;

            if (load.force1) {
                load.force1.visible = visibility;
                load.force1.label.visible = visibility;
            }

            if (load.force2) {
                load.force2.visible = visibility;
                load.force2.label.visible = visibility;
            }
        });
    }

    setForcesScale( newScale ) {
        const basePointForceFactor = this.calculateBasePointLoadScaleFactor( );
        const baseDistributedLoadFactor = this.calculateBaseDistributedLoadScaleFactor( );

        this.pointLoads.forEach(load => {
            load.setScale(newScale * 0.5 * basePointForceFactor);
        });

        this.memberDistributedLoads.forEach(load => {
            load.setScale(newScale * 0.25 * baseDistributedLoadFactor);
        });
    }

    setForcesColor( color ) {
        this.pointLoads.forEach(load => {
            load.setColor( color );
        });

        this.memberDistributedLoads.forEach(load => {
            load.setColor( color );
        });
    }

    setReactionsVisibility( visibility ) {
        this.reactions.forEach(force => {
            force.visible = visibility;
            force.label.visible = visibility;
        });
    }

    setReactionsScale( newScale ) {
        const baseFactor = this.calculateBaseReactionScaleFactor( );
        this.reactions.forEach(force => {
            force.setScale( newScale * 0.3 * baseFactor );
        });
    }

    setReactionsColor( color ) {
        this.reactions.forEach(force => {
            force.setColor( color );
        });
    }

    setAxialForcesVisibility( visibility ) {
        this.results.axialForces.forEach(force => {
            force.visible = visibility;

            force.labels.forEach(label => {
                label.visible = visibility;
            });
        });
    }

    setAxialForcesScale( newScale ) {
        const baseFactor = this.calculateBaseStressScaleFactor( this.results.axialForcesValues );

        this.results.axialForces.forEach(stress => {
            stress.setScale( newScale * 0.2 * baseFactor );
        });
    }

    setAxialForcesColor( color ) {
        this.results.axialForces.forEach(force => {
            force.setColor( color );
        });
    }

    setShearForcesVisibility( visibility ) {
        this.results.shearForces.forEach(force => {
            force.visible = visibility;

            force.labels.forEach(label => {
                label.visible = visibility;
            });
        });
    }

    setShearForcesScale( newScale ) {
        const baseFactor = this.calculateBaseStressScaleFactor( this.results.shearForcesValues );

        this.results.shearForces.forEach(stress => {
            stress.setScale( newScale * 0.25 * baseFactor );
        });
    }

    setShearForcesColor( color ) {
        this.results.shearForces.forEach(force => {
            force.setColor( color );
        });
    }

    setBendingMomentsVisibility( visibility ) {
        this.results.bendingMoments.forEach(force => {
            force.visible = visibility;

            force.labels.forEach(label => {
                label.visible = visibility;
            });
        });
    }

    setBendingMomentsScale( newScale ) {
        const baseFactor = this.calculateBaseStressScaleFactor( this.results.bendingMomentsValues );
        this.results.bendingMoments.forEach(stress => {
            stress.setScale( newScale * 0.3 * baseFactor );
        });
    }

    setBendingMomentsColor( color ) {
        this.results.bendingMoments.forEach(force => {
            force.setColor( color );
        });
    }

    setDeformationsVisibility( visibility ) {
        this.results.deformations.forEach(line => {
            line.visible = visibility;
        });
    }

    setDeformationsScale( newScale ) {
        this.results.deformations.forEach(line => {
            line.setScale( newScale );
        });
    }

    setDeformationsColor( color ) {
        this.results.deformations.forEach(line => {
            line.setColor( color );
        });
    }

    loadModel( model ) {
        // console.log('model', model);
        this.clearModel();

        if (model?.nodes) {
            this.loadNodes( model.nodes );
        }

        if (model?.members) {
            this.loadMembers( model.members );
        }

        if (model?.dims) {
            this.loadDimensions( model.dims );
        }

        this.applySettings();
    }

    loadNodes( nodes ) {

        if (nodes.length === 0) {
            console.info('No nodes to load');
            return;
        }

        nodes.forEach( ( nodeData ) => {
            const newNode = new Node(nodeData.coords, this.materialManager.materials.node, nodeData.label);
            this.nodesMap.set(nodeData.label, newNode);

            const newSupport = new Support(nodeData.fixity, this.materialManager.materials.support);
            this.supportsMap.set(nodeData.label, newSupport);

            newNode.add( newSupport );
            this.scene.add( newNode );
        });
    }

    loadMembers( members ) {
        if (members.length === 0) {
            console.info('No members to load');
            return;
        }

        members.forEach( (memberData) => {
            const newMember = new Member(
                this.nodesMap.get(memberData.start_node),
                this.nodesMap.get(memberData.end_node),
                this.materialManager.materials.member,
                memberData.label
            );

            this.membersMap.set(memberData.label, newMember);

            if (memberData.hinges[0]) {
                const startHinge = new Hinge(0, this.materialManager.materials.support);
                this.supportsMap.set(`${memberData.label}S`, startHinge);
                newMember.add(startHinge);
            }

            if (memberData.hinges[1]) {
                const endHinge = new Hinge(newMember.length, this.materialManager.materials.support);
                this.supportsMap.set(`${memberData.label}E`, endHinge);
                newMember.add(endHinge);
            }

            this.scene.add( newMember );
        } );
    }

    loadDimensions( dims ) {
        if (dims.length === 0) {
            console.info('No dimensions to load');
            return;
        }
        dims.forEach( (dimData) => {
            const extrude = dimData.extrude;
            for (let i = 0; i < dimData.points.length - 1; i++) {
                let dimLabel = dimData?.rebarLabel || '';
                this.addDimension(dimData.points[i], dimData.points[i + 1], extrude, dimLabel);
            }
        });
    }

    drawLoads( model, activeLoadCase ) {
        this.clearLoads();

        if (model?.load_cases?.[activeLoadCase] ) {
            this.drawLoadCase(model.load_cases[activeLoadCase]);
        }

        this.applySettings();
    }

    drawResults( feaResults, activeCase, isEnvelope = false, resistanceResults = null ) {
        // console.log('results', feaResults);
        this.clearResults();

        const results = feaResults?.results?.[activeCase] || {};

        if (!isEnvelope) {
            if (results?.node_results) {
                this.drawReactions( results.node_results );
            }

            if (results?.member_results) {
                this.drawStresses(
                    results.member_results,
                );

                this.drawDeformations(
                    results.member_results,
                );
            }

        } else {
            this.drawEnvelopes( results );
        }

        if (resistanceResults) {
            this.drawResistanceStresses( resistanceResults );
        }

        this.applySettings();
    }

    drawLoadCase( loadCase ) {
        if (loadCase?.nodal_loads) {
            this.drawNodalLoads( loadCase.nodal_loads );
        }

        if (loadCase?.member_point_loads) {
            this.drawMemberPointLoads( loadCase.member_point_loads );
        }

        if (loadCase?.member_distributed_loads) {
            this.drawDistributedLoads( loadCase.member_distributed_loads );
        }
    }

    drawNodalLoads( nodalLoads ) {
        nodalLoads.forEach((nodalLoad) => {
            const forceComponents = nodalLoad.components.slice(0, 3);
            const momentComponents = nodalLoad.components.slice(3, 6);

            const dim = nodalLoad.dimension;

            const node = this.nodesMap.get(nodalLoad.target);

            forceComponents.forEach((component, i) => {
                if (!isCloseToZero(component)) {
                    let components = [0, 0, 0];
                    components[i] = component;

                    let text = `${Math.abs(component).toFixed(2)} kN`;

                    if (dim === 'line') {
                        text += '/m';
                    }

                    const nodalForce = new ForceHelper(
                        components,
                        text,
                        this.materialManager.materials.load.line,
                        this.materialManager.materials.load.forceMesh,
                        this.config.settings.forcesColor
                    );

                    node.add( nodalForce );

                    this.pointLoads.push( nodalForce );
                    this.pointLoadValues.push( component );
                }
            });

            momentComponents.forEach((component, i) => {
                if (!isCloseToZero(component)) {
                    let components = [0, 0, 0];
                    components[i] = component;

                    let text = `${Math.abs(component).toFixed(2)} kNm`;

                    if (dim === 'line') {
                        text += '/m';
                    }

                    const nodalMoment = new MomentHelper(
                        components,
                        text,
                        this.materialManager.materials.load.line,
                        this.materialManager.materials.load.forceMesh,
                        this.config.settings.forcesColor
                    );

                    node.add( nodalMoment );

                    this.pointLoads.push( nodalMoment );
                }
            });
        });
    }

    drawMemberPointLoads( memberPointLoads ) {
        memberPointLoads.forEach((memberPointLoad) => {
            const forceComponents = memberPointLoad.components.slice(0, 3);
            const momentComponents = memberPointLoad.components.slice(3, 6);
            const xLoc = memberPointLoad.x_local;
            const isLocalCS = memberPointLoad.lcs;
            const dim = memberPointLoad.dimension;

            const member = this.membersMap.get(memberPointLoad.target);

            forceComponents.forEach((component, i) => {
                if (!isCloseToZero(component)) {
                    let components = [0, 0, 0];
                    components[i] = component;

                    let text = `${Math.abs(component).toFixed(2)} kN`;

                    if (dim === 'line') {
                        text += '/m';
                    }

                    const memberForce = new ForceHelper(
                        components,
                        text,
                        this.materialManager.materials.load.line,
                        this.materialManager.materials.load.forceMesh,
                        this.config.settings.forcesColor
                    );

                    if (isLocalCS) {
                        member.add( memberForce );
                    } else {
                        member.attach( memberForce );
                    }

                    memberForce.position.set(xLoc, 0, 0);

                    this.pointLoads.push( memberForce );
                    this.pointLoadValues.push( component );
                }
            });

            momentComponents.forEach((component, i) => {
                if (!isCloseToZero(component)) {
                    let components = [0, 0, 0];
                    components[i] = component;

                    let text = `${Math.abs(component).toFixed(2)} kNm`;

                    if (dim === 'line') {
                        text += '/m';
                    }

                    const memberMoment = new MomentHelper(
                        components,
                        text,
                        this.materialManager.materials.load.line,
                        this.materialManager.materials.load.mesh,
                        this.config.settings.forcesColor
                    );

                    if (isLocalCS) {
                        member.add( memberMoment );
                    } else {
                        member.attach( memberMoment );
                    }

                    memberMoment.position.set(xLoc, 0, 0);

                    this.pointLoads.push( memberMoment );
                }
            });
        });
    }

    drawDistributedLoads( memberDistributedLoads ) {
        memberDistributedLoads.forEach((memberDistributedLoad) => {
            const loadComponents = memberDistributedLoad.components;

            const xLoc = memberDistributedLoad.x_local;
            const isLocalCS = memberDistributedLoad.lcs;
            const isProjection = memberDistributedLoad.projection;
            const dim = memberDistributedLoad.dimension;

            const member = this.membersMap.get(memberDistributedLoad.target);

            for (let i = 0; i < 3; i++) {
                const component1 = loadComponents[i];
                const component2 = loadComponents[i + 3];

                if (!(isCloseToZero(component1) && isCloseToZero(component2))) {
                    let texts = [
                        `${Math.abs(component1).toFixed(2)} kN/m`,
                        `${Math.abs(component2).toFixed(2)} kN/m`
                    ];

                    if (dim === 'area') {
                        texts = texts.map(text => text + '²');
                    }

                    const axesComponents = [0, 0, 0, 0, 0, 0];
                    axesComponents[i] = component1;
                    axesComponents[i + 3] = component2;

                    const distributedLoad = new DistributedLoadHelper(
                        axesComponents,
                        xLoc,
                        texts,
                        isLocalCS,
                        isProjection,
                        member,
                        this.materialManager.materials.load.line,
                        this.materialManager.materials.load.mesh,
                        this.materialManager.materials.load.forceMesh,
                    );

                    this.scene.add( distributedLoad );
                    this.memberDistributedLoads.push( distributedLoad );
                    this.memberDistributedLoadValues.push( component1, component2 );
                }
            }
        });
    }

    drawReactions( nodeResults ) {
        for (const nodeKey in nodeResults ) {
            const node = this.nodesMap.get(nodeKey);
            const reactionComponents = nodeResults[nodeKey].reactions;

            for (const reactionKey in reactionComponents) {
                const reactionComponent = reactionComponents[reactionKey];
                if (!isCloseToZero(reactionComponent)) {
                    let components = [0, 0, 0];
                    let text = `${Math.abs(reactionComponent).toFixed(2)}`;
                    let reactionObject;

                    if (reactionKey === 'x') {
                        components[0] = reactionComponent;
                        text += ' kN';
                        reactionObject = new ForceHelper(
                            components,
                            text,
                            this.materialManager.materials.reaction.line,
                            this.materialManager.materials.reaction.mesh,
                            this.config.settings.reactionsColor
                        );
                    } else if (reactionKey === 'z') {
                        components[2] = reactionComponent;
                        text += ' kN';
                        reactionObject = new ForceHelper(
                            components,
                            text,
                            this.materialManager.materials.reaction.line,
                            this.materialManager.materials.reaction.mesh,
                            this.config.settings.reactionsColor
                        );
                    } else if (reactionKey === "ry") {
                        components[1] = reactionComponent;
                        text += ' kNm';
                        reactionObject = new MomentHelper(
                            components,
                            text,
                            this.materialManager.materials.reaction.line,
                            this.materialManager.materials.reaction.mesh,
                            this.config.settings.reactionsColor
                        );
                    }

                    node.add( reactionObject );
                    this.reactions.push( reactionObject );

                    if (!(reactionKey.includes('r'))) {
                        this.reactionValues.push( reactionComponent );
                    }
                }
            }
        }
    }

    drawStresses( memberResults ) {
        for (const memberKey in memberResults) {
            const member = this.membersMap.get(memberKey);
            const memberInternalForces = memberResults[memberKey].internal_forces;

            const xLoc = memberResults[memberKey].x_local;

            const axialForces = memberInternalForces.axial_forces.all;
            const shearForces = memberInternalForces.shear_forces.all;
            const bendingMoments = memberInternalForces.bending_moments.all;

            const xPeaks = memberInternalForces.x_peaks;
            const peakAxialForces = memberInternalForces.axial_forces.extreme;
            const peakShearForces = memberInternalForces.shear_forces.extreme;
            const peakBendingMoments = memberInternalForces.bending_moments.extreme;

            const axF = new InternalForceHelper(
                xLoc,
                axialForces,
                peakAxialForces,
                xPeaks,
                'axial',
                this.materialManager.materials.axialForce.line,
                this.materialManager.materials.axialForce.mesh,
                this.config.settings.axialForcesColor
            );

            member.add( axF );
            this.results.axialForces.push( axF );
            this.results.axialForcesValues.push( ...peakAxialForces );

            const shF = new InternalForceHelper(
                xLoc,
                shearForces,
                peakShearForces,
                xPeaks,
                'shear',
                this.materialManager.materials.shearForce.line,
                this.materialManager.materials.shearForce.mesh,
                this.config.settings.shearForcesColor
            );

            member.add( shF );
            this.results.shearForces.push( shF );
            this.results.shearForcesValues.push( ...peakShearForces );

            const bM = new InternalForceHelper(
                xLoc,
                bendingMoments,
                peakBendingMoments,
                xPeaks,
                'moment',
                this.materialManager.materials.bendingMoment.line,
                this.materialManager.materials.bendingMoment.mesh,
                this.config.settings.bendingMomentsColor
            );
            member.add( bM );
            this.results.bendingMoments.push( bM );
            this.results.bendingMomentsValues.push( ...peakBendingMoments );
        }
    }

    drawEnvelopes( results ) {
        const memberResults = results.member_results;
        const nodeResults = results.node_results;

        for (const nodeKey in nodeResults) {
            const node = this.nodesMap.get(nodeKey);
            const reactionComponents = nodeResults[nodeKey].reactions;

            for (const reactionKey in reactionComponents) {
                reactionComponents[reactionKey].forEach(reaction => {
                    // console.log("reaction", reaction);
                    if (reaction !== null && !isCloseToZero(reaction)) {
                        let components = [0, 0, 0];
                        let text = `${Math.abs(reaction).toFixed(2)}`;
                        let reactionObject;

                        if (reactionKey === 'x') {
                            components[0] = reaction;
                            text += ' kN';
                            reactionObject = new ForceHelper(
                                components,
                                text,
                                this.materialManager.materials.reaction.line,
                                this.materialManager.materials.reaction.mesh,
                                this.config.settings.reactionsColor
                            );
                        } else if (reactionKey === 'z') {
                            components[2] = reaction;
                            text += ' kN';
                            reactionObject = new ForceHelper(
                                components,
                                text,
                                this.materialManager.materials.reaction.line,
                                this.materialManager.materials.reaction.mesh,
                                this.config.settings.reactionsColor
                            );
                        } else if (reactionKey === "ry") {
                            components[1] = reaction;
                            text += ' kNm';
                            reactionObject = new MomentHelper(
                                components,
                                text,
                                this.materialManager.materials.reaction.line,
                                this.materialManager.materials.reaction.mesh,
                                this.config.settings.reactionsColor
                            );
                        }

                        node.add( reactionObject );
                        this.reactions.push( reactionObject );

                        if (!(reactionKey.includes('r'))) {
                            this.reactionValues.push( reaction );
                        }
                    }
                });
            }
        }

        for (const memberKey in memberResults) {
            const member = this.membersMap.get(memberKey);
            const memberInternalForces = memberResults[memberKey].internal_forces;

            const xLoc = memberResults[memberKey].x_local;

            const axialForces = memberInternalForces.axial_forces.all;
            const shearForces = memberInternalForces.shear_forces.all;
            const bendingMoments = memberInternalForces.bending_moments.all;

            const xAxialPeaks = memberInternalForces.axial_forces.extreme.x_local;
            const xShearPeaks = memberInternalForces.shear_forces.extreme.x_local;
            const xMomentPeaks = memberInternalForces.bending_moments.extreme.x_local;
            const peakAxialForces = memberInternalForces.axial_forces.extreme.values;
            const peakShearForces = memberInternalForces.shear_forces.extreme.values;
            const peakBendingMoments = memberInternalForces.bending_moments.extreme.values;

            const xDisplacements = memberResults[memberKey].displacements.x;
            const zDisplacements = memberResults[memberKey].displacements.z;
            const peakDisplacements = [memberResults[memberKey].displacements.extreme, null];

            for (let i = 0; i < 2; i++) {
                const axF = new InternalForceHelper(
                    xLoc,
                    axialForces[i],
                    [peakAxialForces[i]],
                    [xAxialPeaks[i]],
                    'axial',
                    this.materialManager.materials.axialForce.line,
                    this.materialManager.materials.axialForce.mesh,
                    this.config.settings.axialForcesColor
                );

                member.add( axF );
                this.results.axialForces.push( axF );
                this.results.axialForcesValues.push( ...peakAxialForces );

                const shF = new InternalForceHelper(
                    xLoc,
                    shearForces[i],
                    [peakShearForces[i]],
                    [xShearPeaks[i]],
                    'shear',
                    this.materialManager.materials.shearForce.line,
                    this.materialManager.materials.shearForce.mesh,
                    this.config.settings.shearForcesColor
                );

                member.add( shF );
                this.results.shearForces.push( shF );
                this.results.shearForcesValues.push( ...peakShearForces );

                const bM = new InternalForceHelper(
                    xLoc,
                    bendingMoments[i],
                    [peakBendingMoments[i]],
                    [xMomentPeaks[i]],
                    'moment',
                    this.materialManager.materials.bendingMoment.line,
                    this.materialManager.materials.bendingMoment.mesh,
                    this.config.settings.bendingMomentsColor
                );
                member.add( bM );
                this.results.bendingMoments.push( bM );
                this.results.bendingMomentsValues.push( ...peakBendingMoments );

                const defLine = new DeformedStructureHelper(
                    member,
                    xLoc,
                    {x: xDisplacements[i], z: zDisplacements[i], extreme: peakDisplacements[i]},
                    this.materialManager.materials.deformation.line,
                    this.config.settings.deformationsColor,
                );

                this.results.deformations.push( defLine );
                this.results.deformationValues.push( ...xDisplacements[i], ...zDisplacements[i] );
                member.add(defLine);
            }
        }
    }

    drawDeformations( memberResults ) {
        for (const memberKey in memberResults) {
            const member = this.membersMap.get(memberKey);
            const displacements = memberResults[memberKey].displacements;
            const xLoc = memberResults[memberKey].x_local;

            const defLine = new DeformedStructureHelper(
                member,
                xLoc,
                displacements,
                this.materialManager.materials.deformation.line,
                this.config.settings.deformationsColor,
            );

            this.results.deformations.push( defLine );
            this.results.deformationValues.push( ...displacements.x, ...displacements.z );
            member.add(defLine);
        }
    }

    drawResistanceStresses( resistanceStresses ) {
        for (const memberKey in resistanceStresses) {
            const member = this.membersMap.get(memberKey);
            const mbrResistanceStresses = resistanceStresses[memberKey];

            const xLoc = mbrResistanceStresses.x_loc_resistance;

            const shearForces = [
                mbrResistanceStresses.v_rd_pos,
                mbrResistanceStresses.v_rd_neg,
            ];
            const bendingMoments = [
                mbrResistanceStresses.m_rd_pos,
                mbrResistanceStresses.m_rd_neg,
            ];

            const xShearPeaks = [
                xLoc,
                xLoc,
            ];
            const xMomentPeaks = xShearPeaks;
            const peakShearForces = shearForces;
            const peakBendingMoments = bendingMoments;

            for (let i = 0; i < 2; i++) {
                const shF = new InternalForceHelper(
                    xLoc,
                    shearForces[i],
                    [peakShearForces[i]][0],
                    [xShearPeaks[i]][0],
                    'shear',
                    this.materialManager.materials.shearForce.limitLine,
                    this.materialManager.materials.shearForce.limitMesh,
                    this.config.settings.shearForcesColor
                );

                member.add( shF );
                this.results.shearForces.push( shF );
                // this.results.shearForcesValues.push( ...peakShearForces );

                const bM = new InternalForceHelper(
                    xLoc,
                    bendingMoments[i],
                    [peakBendingMoments[i]][0],
                    [xMomentPeaks[i]][0],
                    'moment',
                    this.materialManager.materials.bendingMoment.limitLine,
                    this.materialManager.materials.bendingMoment.limitMesh,
                    this.config.settings.bendingMomentsColor
                );
                member.add( bM );
                this.results.bendingMoments.push( bM );
                // this.results.bendingMomentsValues.push( ...peakBendingMoments );
            }
        }
        this.applySettings();
    }

    clearModel() {
        this.nodesMap.forEach((node, label) => {
            node.clear();
            this.scene.remove(node.mesh);
            this.nodesMap.delete(label);
        });

        this.membersMap.forEach((member, label) => {
            member.clear();
            this.scene.remove(member.mesh);
            this.membersMap.delete(label);
        });

        this.dimensionsMap.forEach((dimension, uuid) => {
            dimension.clear();
            this.scene.remove(dimension);
            this.dimensionsMap.delete(uuid);
        });
    }

    clearLoads() {
        this.pointLoads.forEach(load => {
            load.clear();
            this.scene.remove(load.mesh);
        });

        this.memberDistributedLoads.forEach(load => {
            load.clear();
            this.scene.remove(load);
        });

        this.pointLoads = [];
        this.pointLoadValues = [];
        this.memberDistributedLoads = [];
        this.memberDistributedLoadValues = [];
    }

    clearResults() {
        this.reactions.forEach(reaction => {
            reaction.clear();
            this.scene.remove(reaction.mesh);
        });

        this.results.axialForces.forEach(force => {
            force.clear();
            this.scene.remove(force.mesh);
        });

        this.results.shearForces.forEach(force => {
            force.clear();
            this.scene.remove(force.mesh);
        });

        this.results.bendingMoments.forEach(force => {
            force.clear();
            this.scene.remove(force.mesh);
        });

        this.results.deformations.forEach(line => {
            line.clear();
            this.scene.remove(line.geometry);
        });

        this.reactions = [];
        this.reactionValues = [];

        this.results = {
            axialForces: [],
            axialForcesValues: [],
            shearForces: [],
            shearForcesValues: [],
            bendingMoments: [],
            bendingMomentsValues: [],
            deformations: [],
            deformationValues: [],
        };
    }

    zoomToFit( object ) {
        // Get the bounding box of the object
        const boundingBox = new THREE.Box3();

        const excludeObjects = [this.axesHelperOrigin, this.scene, this.grid];

        // Exclude AxisHelper from the bounding box
        object.traverse((obj) => {
            if (!excludeObjects.includes(obj) && obj.visible) {
                boundingBox.expandByObject(obj);
            }
        });

        if (boundingBox.isEmpty()) {
            console.info('zoomToFit: Bounding box is empty.', boundingBox);
            return;
        }

        const padding = 0.5;

        this.cameraControls.fitToBox(
            boundingBox,
            true,
            {
                cover: false,
                paddingLeft: padding,
                paddingRight: padding,
                paddingBottom: padding,
                paddingTop: padding,
            }
        );
    }

    addDimension(from, to, extrude, rebarLabel) {
        const fromVector = new THREE.Vector3(...from);
        const toVector = new THREE.Vector3(...to);

        const newDimension = new LinearDimension(fromVector, toVector, extrude, rebarLabel, this.materialManager.materials.dims);
        this.dimensionsMap.set(newDimension.uuid, newDimension);
        this.scene.add(newDimension);
    }

    calculateBasePointLoadScaleFactor(  ) {
        // Find the largest absolute value among all components
        const biggestAbsoluteValue = Math.max(...this.pointLoadValues.map(Math.abs));
        if (isCloseToZero(biggestAbsoluteValue)) {
            return 1; // Avoid division by zero
        }
        const nom = this.getNominatorForScaling();
        return nom / biggestAbsoluteValue;
    }

    calculateBaseDistributedLoadScaleFactor( ) {
        const biggestAbsoluteValue = Math.max(...this.memberDistributedLoadValues.map(Math.abs));
        if (biggestAbsoluteValue === 0) {
            return 1;
        }
        const nom = this.getNominatorForScaling();
        return  nom  / biggestAbsoluteValue;
    }

    calculateBaseReactionScaleFactor() {
        const biggestAbsoluteValue = Math.max(...this.reactionValues.map(Math.abs));
        if (biggestAbsoluteValue === 0) {
            return 1;
        }
        const nom = this.getNominatorForScaling();
        return nom / biggestAbsoluteValue;
    }

    calculateBaseStressScaleFactor( peakValues ) {
        const biggestAbsoluteValue = Math.max(...peakValues.map(Math.abs));
        if (biggestAbsoluteValue === 0) {
            return 1;
        }
        const nom = this.getNominatorForScaling();
        return nom / biggestAbsoluteValue;
    }

    getNominatorForScaling() {
        const nodeValues = Array.from(this.nodesMap.values());
        const zCoords = nodeValues.map(node => node.coords[2]);
        const maxZ = Math.max(...zCoords);
        const minZ = Math.min(...zCoords);
        return Math.max(2, maxZ - minZ);
    }
}

function isCloseToZero(value, epsilon = 1e-6) {
    return Math.abs(value) < epsilon;
}

export { Viewer };